import {ChangeDetectionStrategy, Component, OnDestroy} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {LauncherService} from "../../services/launcher.service";
import {CoreRouteParam} from "../../constants/core-routes.enum";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  delayWhen,
  filter,
  map,
  of,
  switchMap,
  tap,
  timeout, timer
} from "rxjs";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {GlobalErrorHandler} from "../../errors/global-error-handler";

const GET_REDIRECTION_URL_REQUEST_TIMEOUT = 1000 * 30;
const ANIMATION_DURATION = 150;

@Component({
  selector: 'app-redirection-loader',
  templateUrl: './redirection-loader.component.html',
  styleUrls: ['./redirection-loader.component.scss'],
  providers: [LauncherService, GlobalErrorHandler],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('opacityGrow', [
      state('false', style({
        opacity: '0',
      })),
      state('true', style({
        opacity: '1'
      })),
      transition('* => *', animate(ANIMATION_DURATION))
    ]),
  ]
})
export class RedirectionLoaderComponent implements OnDestroy {


  private readonly fetchRedirectionUrlBS: BehaviorSubject<any> = new BehaviorSubject<any>(true);
  private readonly showRetryInfoBS: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly mhbAccessHash$ = this.route.params.pipe(map(params => params[CoreRouteParam.AccessHash]));

  readonly redirectionUrlLoaded$ = combineLatest([this.mhbAccessHash$, this.fetchRedirectionUrlBS]).pipe(
    tap(_ => this.showRetryInfoBS.next(false)),
    switchMap(([mhbAccessHash, _]) => this.launcherService.getRedirectionUrl$(mhbAccessHash).pipe(
      timeout(GET_REDIRECTION_URL_REQUEST_TIMEOUT),
      catchError((err, caught) => {
        this.showRetryInfoBS.next(true);
        this.globalErrorHandler.handleError(err);
        return of(null);
      })
    )),
    filter(Boolean),
    tap(response => window.location.href = response.link)
  );

  readonly loaderAnimationState$ = this.showRetryInfoBS.asObservable().pipe(
    map(retryVisible => !retryVisible)
  )
  readonly retryInfoVisible$ = this.showRetryInfoBS.asObservable().pipe(
    delayWhen(retryVisible => retryVisible ? timer(ANIMATION_DURATION) : of(undefined))
  )

  constructor(
    private route: ActivatedRoute,
    private launcherService: LauncherService,
    private globalErrorHandler: GlobalErrorHandler
  ) {
  }

  ngOnDestroy() {
    this.fetchRedirectionUrlBS.complete();
    this.showRetryInfoBS.complete();
  }

  onRetryClick(): void {
    this.fetchRedirectionUrlBS.next(true);
  }

}
